import type { CSSProperties } from 'react'
import { usePopupLayer } from './popupLayers'
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
  type ReactNode,
} from 'react'
import clsx from 'clsx'
import type { StatusTone } from './pageHeaderStatusTones'
import { ValueSelectorTrigger, type ValueSelectorVariant } from './ValueSelectorTrigger'
import { ValueSelectorDropdown, type ValueSelectorDropdownPosition } from './ValueSelectorDropdown'
import {
  MENU_OPTION_GAP_PX,
  VIEW_PARAMETER_EMPTY_VALUE_OPTION_CLASS,
  VIEW_PARAMETER_VALUE_OPTION_CLASS,
} from './compactDropdownStyles'
import {
  getContentSizedDropdownWidth,
  getViewportAwareDropdownHorizontalPosition,
  getViewportAwareDropdownPosition,
} from './dropdownPositioning'
import { measureMaxTextWidth } from '../utils/measureTextWidth'
import { useMeasuredElementFont } from '../hooks/useMeasuredElementFont'
import { useOutsideMouseDown } from '../hooks/useOutsideMouseDown'
import { useFollowAnchor } from '../hooks/useFollowAnchor'
import { useSelectorSearchFocus } from './useSelectorSearchFocus'

// Trigger/dropdown chrome: chevron, horizontal padding, focus ring adjustment,
// and the compact dropdown gutter. Keep this close to the rendered control so
// fit-to-content selectors do not carry a large trailing tail after the label.
const VALUE_SELECTOR_CHROME_PX = 40
const VALUE_SELECTOR_MIN_WIDTH_PX = 120
const VALUE_SELECTOR_MAX_WIDTH_PX = 320
const VALUE_SELECTOR_BOTTOM_OFFSET_PX = 16
const VALUE_SELECTOR_DROPDOWN_CHROME_PX = 8
const VALUE_SELECTOR_SEARCH_CHROME_PX = 44
const FIELD_VALUE_SELECTOR_SEARCH_CHROME_PX = 41
const CELL_VALUE_SELECTOR_OPTION_LIST_CHROME_PX = 12
/** The p-1 wrapper INSIDE the field dropdown's scroll content. */
const FIELD_VALUE_SELECTOR_LIST_PADDING_PX = 8
const FIELD_VALUE_SELECTOR_OPTION_HEIGHT_PX = 36
const TOOLBAR_VALUE_SELECTOR_OPTION_HEIGHT_PX = 32
const VIEW_PARAMETER_VALUE_SELECTOR_OPTION_HEIGHT_PX = 28
const TOOLBAR_VALUE_SELECTOR_MEASUREMENT_FONT = '400 16px "Inter Variable", system-ui, sans-serif'
const FIELD_VALUE_SELECTOR_MEASUREMENT_FONT = '450 16px "Inter Variable", system-ui, sans-serif'
const CELL_VALUE_SELECTOR_MEASUREMENT_FONT = '400 14px "Inter Variable", system-ui, sans-serif'
const VIEW_PARAMETER_VALUE_SELECTOR_MEASUREMENT_FONT =
  '400 12px "Inter Variable", system-ui, sans-serif'

export interface ValueSelectorOption {
  /** Display label shown to the user. */
  label: string
  /** Optional second line beneath the label, also shown in the selected value. */
  description?: string
  /** Underlying identifier sent on selection (e.g., FK UUID). */
  id: string
  /** `danger` for a destructive choice (Delete), `warning` for a one-way one (Detach). */
  tone?: StatusTone
  /** Optional display treatment for immutable/system values. */
  italic?: boolean
  /** Optional muted display treatment for empty/inherited options. */
  muted?: boolean
  /** Optional right-aligned helper label, such as "Default". */
  rightLabel?: string
  /** Optional adornment rendered before the label (e.g., a status dot). */
  leading?: ReactNode
  /** Optional secondary label rendered with lower visual emphasis. */
  secondaryLabel?: string
  /** Optional adornment rendered before the secondary label. */
  secondaryLeading?: ReactNode
  /** Optional text used for filtering in addition to the visible labels. */
  searchText?: string
  disabled?: boolean
  disabledReason?: string
  /**
   * A second target at the row's end that closes the list and runs instead of
   * selecting; Right arrow reaches it from the keyboard.
   */
  trailingAction?: ValueSelectorOptionAction
  /**
   * The kinds this option groups ("Count" over All, Distinct, …): it opens
   * them in a list beside it instead of being chosen itself. A search lists
   * them in its place, by `label`.
   */
  children?: ValueSelectorOption[]
  /** Its name in its parent's side list ("Distinct" under Count); `label` everywhere else. */
  shortLabel?: string
  /**
   * An info icon at the row's right, explaining it (and at a field trigger's
   * right while chosen); pressing the icon doesn't choose the option.
   */
  hint?: string
}

/** Every choosable option: a grouping option's kinds in its place. */
// eslint-disable-next-line react-refresh/only-export-components
export function flattenValueSelectorOptions(options: ValueSelectorOption[]): ValueSelectorOption[] {
  return options.flatMap((option) => option.children ?? [option])
}

export interface ValueSelectorOptionAction {
  label: string
  icon: ReactNode
  onSelect: () => void
}

interface ValueSelectorProps {
  id?: string
  options: ValueSelectorOption[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  /** The trigger placeholder's color (replacing the default), e.g. a danger tone for a required field. */
  placeholderClassName?: string
  emptyLabel?: string
  emptyOptionsMessage?: string
  /** Non-selectable status below available options while more choices load. */
  loadingMessage?: string
  className?: string
  controlClassName?: string
  fullWidth?: boolean
  minWidthPx?: number
  maxWidthPx?: number
  allowEmpty?: boolean
  clearable?: boolean
  emptyOptionAriaLabel?: string
  /**
   * Whether the closed selector repeats the chosen option's description under
   * its label. Off where descriptions only help choose; a cell never shows it.
   */
  showSelectedDescription?: boolean
  /**
   * Each option is a card in the list: its name over its whole description,
   * so a choice explains itself without a tip. The closed field keeps to one
   * line, with the chosen option's description as its tip. For a few choices
   * that need explaining (an email step's Send).
   */
  optionCards?: boolean
  variant?: ValueSelectorVariant
  inlineLabel?: string
  dropdownZIndex?: CSSProperties['zIndex']
  /**
   * Explicit search control. Defaults by option count: search appears only
   * when the list has at least `SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS` entries.
   */
  showSearch?: boolean
  searchMode?: 'dropdown' | 'trigger'
  openOnMount?: boolean
  dropdownMaxHeightPx?: number
  dropdownBottomOffsetPx?: number
  /**
   * How wide the open list is. `content` (the default): at least the
   * trigger's width, and as wide as its longest option needs, so every option
   * reads in full while choosing. `trigger`: exactly the trigger's width, its
   * options cut off; only where a list must not grow past its field.
   */
  dropdownWidth?: 'trigger' | 'content'
  dropdownAlign?: 'left' | 'right'
  disabled?: boolean
  disabledTooltip?: ReactNode
  ariaLabel?: string
  ariaInvalid?: boolean
  ariaRequired?: boolean
  /** Ids of the text that explains the control, such as why it is locked. */
  ariaDescribedBy?: string
  onBlur?: () => void
  /** Observe opening/dismissal, including keyboard and outside clicks. */
  onOpenChange?: (open: boolean) => void
  /**
   * `chevron`: only a click near the chevron opens the list (within
   * `chevronHitWidthPx` of the anchor's trailing edge), or `openRequest`; any
   * click closes it, and its keys are left to the host. A table cell uses
   * this: a click elsewhere selects the cell, the arrow keys move past it,
   * and the grid opens it on a double-click or Enter.
   */
  openTrigger?: 'interaction' | 'chevron'
  /** How far in from the trailing edge a click opens a `chevron` trigger. */
  chevronHitWidthPx?: number
  /** Opens the list each time it increases, as when a grid cell takes Enter. */
  openRequest?: number
  onAutoWidthChange?: (widthPx: number) => void
  /**
   * Fully custom trigger rendered inside the anchored container in place of
   * the built-in control. The portaled dropdown still anchors to the
   * container, so it spans exactly the custom trigger's width. Implies
   * dropdown search; trigger-mode search is unsupported here.
   */
  renderTrigger?: (args: { open: boolean; toggle: () => void }) => ReactNode
  /**
   * The host searches: each change of the search text comes here (and an
   * empty one when the list closes), and `options` arrive already narrowed,
   * so the list doesn't filter them again. For lists too long to load whole
   * (a table's records).
   */
  onSearchChange?: (search: string) => void
  /** The chosen option, for a list that may not hold it (one the host searches or pages). */
  selectedOption?: ValueSelectorOption
}

/** Short lists read faster without a search box; long lists need one. */
export const SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS = 8

function normalizeWidthBounds(minWidthPx: number, maxWidthPx: number) {
  return {
    min: Math.min(minWidthPx, maxWidthPx),
    max: Math.max(minWidthPx, maxWidthPx),
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function getValueSelectorAutoWidthPx({
  options,
  placeholder,
  minWidthPx = VALUE_SELECTOR_MIN_WIDTH_PX,
  maxWidthPx = VALUE_SELECTOR_MAX_WIDTH_PX,
  variant = 'toolbar',
  measurementFont,
}: {
  options: ValueSelectorOption[]
  placeholder: string
  minWidthPx?: number
  maxWidthPx?: number
  variant?: ValueSelectorVariant
  measurementFont?: string | null
}) {
  const widthBounds = normalizeWidthBounds(minWidthPx, maxWidthPx)
  if (options.length === 0) return widthBounds.min
  const labels = [
    ...options.map((o) => (o.rightLabel ? `${o.label} ${o.rightLabel}` : o.label)),
    placeholder,
  ]
  const font =
    measurementFont ??
    (variant === 'field'
      ? FIELD_VALUE_SELECTOR_MEASUREMENT_FONT
      : variant === 'viewParameter'
        ? VIEW_PARAMETER_VALUE_SELECTOR_MEASUREMENT_FONT
        : variant === 'cell' || variant === 'compound'
          ? CELL_VALUE_SELECTOR_MEASUREMENT_FONT
          : TOOLBAR_VALUE_SELECTOR_MEASUREMENT_FONT)
  const maxLabel = measureMaxTextWidth(labels, font)
  return Math.min(
    widthBounds.max,
    Math.max(widthBounds.min, Math.ceil(maxLabel) + VALUE_SELECTOR_CHROME_PX)
  )
}

/**
 * Searchable dropdown for picking from a flat list of `{label, id}` pairs.
 * Visual twin of `FieldSelector` so filter rows stay consistent; structurally
 * different because it operates on opaque label/id pairs instead of `Column`.
 *
 * The selected value remains visible in the trigger while the portaled search
 * panel opens below it. The dropdown is portaled to `document.body` so it can
 * escape modal containers; positioning recomputes on resize and ancestor scroll.
 */
export function ValueSelector({
  id,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  placeholderClassName,
  emptyLabel,
  emptyOptionsMessage = 'No values available',
  loadingMessage,
  className,
  controlClassName: extraControlClassName,
  fullWidth = false,
  minWidthPx = VALUE_SELECTOR_MIN_WIDTH_PX,
  maxWidthPx = VALUE_SELECTOR_MAX_WIDTH_PX,
  allowEmpty = false,
  clearable = false,
  emptyOptionAriaLabel = 'Clear selection',
  showSelectedDescription = true,
  optionCards = false,
  variant = 'toolbar',
  inlineLabel,
  dropdownZIndex,
  showSearch,
  searchMode = 'dropdown',
  openOnMount = false,
  dropdownMaxHeightPx,
  dropdownBottomOffsetPx = VALUE_SELECTOR_BOTTOM_OFFSET_PX,
  dropdownWidth = 'content',
  dropdownAlign = 'left',
  disabled = false,
  disabledTooltip,
  ariaLabel,
  ariaInvalid,
  ariaRequired,
  ariaDescribedBy,
  onBlur,
  onOpenChange,
  onAutoWidthChange,
  renderTrigger,
  openTrigger = 'interaction',
  chevronHitWidthPx = 40,
  openRequest = 0,
  onSearchChange,
  selectedOption: knownSelectedOption,
}: ValueSelectorProps) {
  const popupLayer = usePopupLayer()
  const [open, setOpen] = useState(openOnMount)
  const previousOpen = useRef(open)
  useEffect(() => {
    if (previousOpen.current === open) return
    previousOpen.current = open
    onOpenChange?.(open)
  }, [open, onOpenChange])
  const [search, setSearch] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [dropdownPosition, setDropdownPosition] = useState<ValueSelectorDropdownPosition | null>(
    null
  )
  const hasDropdownPosition = dropdownPosition !== null
  const inputRef = useRef<HTMLInputElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const submenuRef = useRef<HTMLDivElement>(null)
  /** The grouping option whose side list is open; `highlight` is the keyboard's place in it. */
  const [submenu, setSubmenu] = useState<{ id: string; highlight: number | null } | null>(null)
  const optionListRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  /** The built-in trigger control, focused when a `chevron` host opens the list. */
  const controlRef = useRef<HTMLInputElement | HTMLButtonElement | HTMLDivElement | null>(null)
  const { measurementFont, setMeasuredElement } = useMeasuredElementFont<
    HTMLInputElement | HTMLButtonElement | HTMLDivElement
  >()
  const setControlElement = useCallback(
    (node: HTMLInputElement | HTMLButtonElement | HTMLDivElement | null) => {
      controlRef.current = node
      setMeasuredElement(node)
    },
    [setMeasuredElement]
  )
  const shouldScrollHighlightedRef = useRef(false)
  const isFieldVariant = variant === 'field'
  const isCellVariant = variant === 'cell'
  const isCompoundVariant = variant === 'compound'
  const isViewParameterVariant = variant === 'viewParameter'
  const searchEnabled = showSearch ?? options.length >= SELECTOR_SEARCH_DEFAULT_MIN_OPTIONS
  const searchInTrigger = searchEnabled && searchMode === 'trigger'
  const showDropdownSearch = searchEnabled && !searchInTrigger

  const filtered = useMemo(() => {
    if (onSearchChange) return options
    const term = search.toLowerCase()
    return (term ? flattenValueSelectorOptions(options) : options)
      .filter((opt) => {
        const searchableText =
          opt.searchText ??
          [opt.label, opt.description, opt.secondaryLabel].filter(Boolean).join(' ')
        return searchableText.toLowerCase().includes(term)
      })
      .sort((a, b) => Number(Boolean(a.disabled)) - Number(Boolean(b.disabled)))
  }, [onSearchChange, options, search])
  const [keyboardNavigation, setKeyboardNavigation] = useState(false)
  const showEmptyOption = allowEmpty && search.trim().length === 0
  const optionCount = filtered.length + (showEmptyOption ? 1 : 0)
  const widthBounds = useMemo(
    () => normalizeWidthBounds(minWidthPx, maxWidthPx),
    [maxWidthPx, minWidthPx]
  )

  // Auto-size to fit the widest option label. Floor + cap prevent runaway
  // widths and keep a usable minimum for empty / short-label lists.
  const autoWidth = useMemo(() => {
    return getValueSelectorAutoWidthPx({
      options: flattenValueSelectorOptions(options),
      placeholder,
      minWidthPx: widthBounds.min,
      maxWidthPx: widthBounds.max,
      variant,
      measurementFont,
    })
  }, [measurementFont, options, placeholder, variant, widthBounds])

  const selectedOption =
    flattenValueSelectorOptions(options).find((opt) => opt.id === value) ??
    (knownSelectedOption?.id === value ? knownSelectedOption : undefined)
  // A one-line cell has no room for it, and some descriptions only help choose.
  const selectedDescription =
    showSelectedDescription && !isCellVariant && !optionCards
      ? selectedOption?.description
      : undefined
  const fieldLikeVariant = isFieldVariant || isCellVariant || isCompoundVariant
  const searchInputClassName = fieldLikeVariant
    ? 'field-input-control block w-full rounded-md border border-[color:var(--input-border)] bg-[color:var(--input-bg)] px-3 py-2 text-sm text-default outline-none transition-colors placeholder:font-light placeholder:italic placeholder:text-muted focus:border-2 focus:border-blue-500 focus:px-[11px] focus:py-[7px]'
    : clsx(
        'block h-8 w-full rounded-md border border-blue-500 px-2 pr-7 outline-none',
        isViewParameterVariant ? 'text-view-parameter-control' : 'text-toolbar'
      )
  const optionClassName = fieldLikeVariant
    ? clsx(
        optionCards
          ? 'flex w-full items-start gap-2 rounded px-3 py-2.5 text-left text-sm text-default'
          : 'flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm text-default',
        isCellVariant && 'table-grid-inline-popover-option'
      )
    : clsx(
        isViewParameterVariant
          ? VIEW_PARAMETER_VALUE_OPTION_CLASS
          : 'flex w-full items-center gap-1.5 rounded-md px-3 py-1.5 text-toolbar text-default'
      )
  const emptyOptionClassName = fieldLikeVariant
    ? clsx(
        'flex min-h-9 w-full items-center rounded py-2 text-sm text-default',
        isCellVariant ? 'table-grid-inline-popover-option gap-2 px-2' : 'gap-1.5 px-3'
      )
    : clsx(
        isViewParameterVariant
          ? VIEW_PARAMETER_EMPTY_VALUE_OPTION_CLASS
          : 'flex min-h-8 w-full items-center gap-1.5 rounded-md px-3 py-1.5 text-toolbar text-default'
      )
  const messageClassName = fieldLikeVariant
    ? 'px-3 py-2 text-sm text-muted'
    : clsx(
        'px-3 py-2 text-muted',
        isViewParameterVariant ? 'text-view-parameter-control' : 'text-toolbar'
      )

  const getDropdownAnchor = useCallback(() => {
    const trigger = containerRef.current
    if (!trigger) return null
    return isCellVariant
      ? (trigger.closest<HTMLElement>('.table-grid-body-cell') ?? trigger)
      : trigger
  }, [isCellVariant])

  const getDropdownAnchorRect = useCallback(() => {
    const rect = getDropdownAnchor()?.getBoundingClientRect()
    if (!rect || !isCellVariant) return rect
    // Grid cells own their trailing border; the leading line belongs to the
    // preceding cell. Shift the attached panel onto that shared line.
    return new DOMRect(rect.x - 1, rect.y, rect.width + 1, rect.height)
  }, [getDropdownAnchor, isCellVariant])
  // The list re-anchors when its field moves on its own (see useFollowAnchor).
  const [anchorMoves, setAnchorMoves] = useState(0)
  useFollowAnchor(open, getDropdownAnchor, () => setAnchorMoves((count) => count + 1))

  useEffect(() => {
    onAutoWidthChange?.(autoWidth)
  }, [autoWidth, onAutoWidthChange])

  // The host's search follows the field's, cleared with it when the list closes.
  useEffect(() => {
    onSearchChange?.(search)
  }, [onSearchChange, search])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setHighlightIndex(0)
      setKeyboardNavigation(false)
      setSubmenu(null)
    }
  }, [open])

  useEffect(() => {
    if (openOnMount) {
      setOpen(true)
    }
  }, [openOnMount])

  const opensFromChevron = openTrigger === 'chevron'
  const openedByRequestRef = useRef(false)
  useEffect(() => {
    if (openRequest > 0 && !disabled) {
      openedByRequestRef.current = true
      setOpen(true)
    }
  }, [disabled, openRequest])
  const toggleFromTrigger = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (opensFromChevron && !open) {
        // Measured from the anchor (a grid cell's own edge), so the target
        // reaches past the trigger into the cell's padding side.
        const anchorRight = (getDropdownAnchor() ?? event.currentTarget).getBoundingClientRect()
          .right
        if (event.clientX < anchorRight - chevronHitWidthPx) return
      }
      setOpen((current) => !current)
    },
    [chevronHitWidthPx, getDropdownAnchor, open, opensFromChevron]
  )

  useEffect(() => {
    if (disabled) {
      setOpen(false)
    }
  }, [disabled])

  // Opened by its host (a grid cell taking Enter, a form starting on it), the
  // list takes the keyboard: its search field when it has one (below), else
  // its trigger.
  useEffect(() => {
    if (open && (opensFromChevron || openedByRequestRef.current) && !searchEnabled)
      controlRef.current?.focus({ preventScroll: true })
    if (!open) openedByRequestRef.current = false
  }, [open, opensFromChevron, searchEnabled])

  useSelectorSearchFocus({
    open,
    enabled: searchEnabled,
    inputRef,
    positionKey: dropdownPosition,
  })

  useOutsideMouseDown(open, () => setOpen(false), containerRef, dropdownRef, submenuRef)

  /** Widest content-sized width measured while open; see the two effects below. */
  const contentWidthRef = useRef<number | null>(null)

  // Anchor portal to the trigger's bounding rect. Recompute on resize and
  // ancestor scroll so the dropdown follows when a modal moves.
  useLayoutEffect(() => {
    if (!open) {
      setDropdownPosition(null)
      contentWidthRef.current = null
      return
    }
    const updatePosition = () => {
      const rect = getDropdownAnchorRect()
      if (!rect) return
      const searchChromeHeight = showDropdownSearch
        ? fieldLikeVariant
          ? FIELD_VALUE_SELECTOR_SEARCH_CHROME_PX
          : VALUE_SELECTOR_SEARCH_CHROME_PX
        : 0
      const frameChromeHeight =
        isViewParameterVariant || !fieldLikeVariant ? VALUE_SELECTOR_DROPDOWN_CHROME_PX : 0
      // Field dropdowns carry their p-1 inset inside the scroll content, so
      // the autofit budget must include it or a scrollbar appears at exact
      // fit. Anyone changing that wrapper must keep this constant in sync.
      const optionListChromeHeight = isCellVariant
        ? CELL_VALUE_SELECTOR_OPTION_LIST_CHROME_PX
        : isFieldVariant
          ? FIELD_VALUE_SELECTOR_LIST_PADDING_PX
          : 0
      const optionRowHeight = fieldLikeVariant
        ? FIELD_VALUE_SELECTOR_OPTION_HEIGHT_PX
        : isViewParameterVariant
          ? VIEW_PARAMETER_VALUE_SELECTOR_OPTION_HEIGHT_PX
          : TOOLBAR_VALUE_SELECTOR_OPTION_HEIGHT_PX
      const panel = dropdownRef.current
      const list = optionListRef.current
      const panelHeight = panel?.getBoundingClientRect().height ?? 0
      const listHeight = list?.getBoundingClientRect().height ?? 0
      const hasRenderedMeasurements = panelHeight > 0 && listHeight > 0
      // Estimate only before the portal mounts. Icons, typography, and wrapped
      // labels can make real rows taller than the default row-height budget.
      const listChromeHeight = hasRenderedMeasurements
        ? Math.max(0, panelHeight - listHeight)
        : frameChromeHeight + searchChromeHeight
      const visibleRowCount = loadingMessage ? optionCount + 1 : Math.max(optionCount, 1)
      const optionGapHeight = Math.max(0, visibleRowCount - 1) * MENU_OPTION_GAP_PX
      const naturalListHeight = hasRenderedMeasurements
        ? list!.scrollHeight
        : optionListChromeHeight +
          optionGapHeight +
          visibleRowCount * optionRowHeight +
          filtered.filter((option) => option.description).length * 16
      const naturalDropdownHeight = listChromeHeight + naturalListHeight
      const desiredDropdownHeight =
        dropdownMaxHeightPx === undefined
          ? naturalDropdownHeight
          : Math.min(
              naturalDropdownHeight,
              dropdownMaxHeightPx + listChromeHeight + optionListChromeHeight
            )
      // This also runs for the dropdown's own list scrolling (the scroll
      // listener is capture-phase), so a content-sized panel must keep the
      // width it measured rather than snapping back to the trigger's.
      const dropdownPosition = getViewportAwareDropdownPosition({
        triggerRect: rect,
        align: dropdownAlign,
        desiredWidthPx:
          dropdownWidth === 'content' ? (contentWidthRef.current ?? undefined) : undefined,
        desiredMaxHeight: desiredDropdownHeight,
        bottomOffsetPx: dropdownBottomOffsetPx,
        gapPx: isCellVariant ? -1 : undefined,
        minUsableHeightPx: listChromeHeight + 80,
      })
      const nextPosition = {
        ...dropdownPosition,
        ...(dropdownWidth === 'content'
          ? getViewportAwareDropdownHorizontalPosition({
              triggerRect: rect,
              align: dropdownAlign,
              // Never the panel's own rendered width; see
              // getContentSizedDropdownWidth.
              desiredWidthPx: Math.max(rect.width, autoWidth, contentWidthRef.current ?? 0),
            })
          : {}),
        maxHeight: Math.max(0, dropdownPosition.maxHeight - listChromeHeight),
        triggerWidth: Math.round(rect.width),
      }
      setDropdownPosition((current) => {
        if (
          current &&
          Object.keys(nextPosition).every(
            (key) =>
              current[key as keyof typeof nextPosition] ===
              nextPosition[key as keyof typeof nextPosition]
          )
        )
          return current
        return nextPosition
      })
    }
    updatePosition()
    const anchor = getDropdownAnchor()
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition)
    if (anchor) resizeObserver?.observe(anchor)
    if (dropdownRef.current) resizeObserver?.observe(dropdownRef.current)
    if (optionListRef.current) {
      resizeObserver?.observe(optionListRef.current)
      // Observe rows as well: their natural height can change while the
      // scroll container remains capped at the same viewport height.
      optionListRef.current
        .querySelectorAll<HTMLElement>('button, [role="status"]')
        .forEach((row) => {
          resizeObserver?.observe(row)
        })
    }
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      resizeObserver?.disconnect()
    }
  }, [
    anchorMoves,
    autoWidth,
    dropdownBottomOffsetPx,
    dropdownMaxHeightPx,
    dropdownWidth,
    dropdownAlign,
    isFieldVariant,
    fieldLikeVariant,
    getDropdownAnchor,
    getDropdownAnchorRect,
    hasDropdownPosition,
    isCellVariant,
    isViewParameterVariant,
    loadingMessage,
    open,
    optionCount,
    filtered,
    showDropdownSearch,
  ])

  useLayoutEffect(() => {
    if (!open || dropdownWidth !== 'content' || !hasDropdownPosition) return
    const panel = dropdownRef.current
    const anchor = getDropdownAnchor()
    if (!panel || !anchor) return

    const measureRenderedWidth = () => {
      const anchorRect = getDropdownAnchorRect()
      if (!anchorRect) return
      const minWidth = Math.max(anchorRect.width, autoWidth)
      // Grow from the width the panel was given (capped to the viewport), by
      // however much a label is cut off. Labels that fit add nothing, so the
      // width settles; nothing here tracks the panel's own rendered width.
      const appliedWidth = Math.min(
        contentWidthRef.current ?? minWidth,
        panel.style.width ? Number.parseFloat(panel.style.width) : Number.POSITIVE_INFINITY
      )
      // A label may cut its text off itself or in a nested part (a name over
      // a description), so each truncating element reports its own overflow.
      const labels = Array.from(
        panel.querySelectorAll<HTMLElement>('[data-value-selector-option-label]')
      ).flatMap((label) =>
        [label, ...Array.from(label.querySelectorAll('*'))]
          .filter((element): element is HTMLElement => element instanceof HTMLElement)
          .map((element) => ({
            contentWidth: element.scrollWidth,
            visibleWidth: element.clientWidth,
          }))
      )

      // Content width only ever grows while open. Shrinking would let a label
      // that just became truncated pull the panel back in, which reads as the
      // panel jittering sideways.
      contentWidthRef.current = Math.max(
        contentWidthRef.current ?? 0,
        getContentSizedDropdownWidth({ appliedWidth, minWidth, labels })
      )
      const horizontalPosition = getViewportAwareDropdownHorizontalPosition({
        triggerRect: anchorRect,
        align: dropdownAlign,
        desiredWidthPx: contentWidthRef.current,
      })
      setDropdownPosition((current) => {
        if (
          !current ||
          (current.left === horizontalPosition.left && current.width === horizontalPosition.width)
        ) {
          return current
        }
        return { ...current, ...horizontalPosition }
      })
    }

    measureRenderedWidth()
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureRenderedWidth)
    resizeObserver?.observe(panel)
    const mutationObserver =
      typeof MutationObserver === 'undefined' ? null : new MutationObserver(measureRenderedWidth)
    mutationObserver?.observe(panel, { childList: true, subtree: true, characterData: true })
    return () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
    }
  }, [
    autoWidth,
    dropdownWidth,
    dropdownAlign,
    getDropdownAnchor,
    getDropdownAnchorRect,
    hasDropdownPosition,
    open,
  ])

  useEffect(() => {
    if (!open || !optionListRef.current || !shouldScrollHighlightedRef.current) return
    shouldScrollHighlightedRef.current = false
    const item = optionListRef.current.querySelectorAll<HTMLElement>(
      '[data-value-selector-option]'
    )[highlightIndex]
    item?.scrollIntoView?.({ block: 'nearest' })
  }, [highlightIndex, open])

  const selectOption = useCallback(
    (opt: ValueSelectorOption) => {
      if (opt.disabled) return
      if (opt.children) {
        setSubmenu({ id: opt.id, highlight: null })
        return
      }
      setOpen(false)
      onChange(opt.id)
    },
    [onChange]
  )
  const submenuParent = submenu ? filtered.find((option) => option.id === submenu.id) : undefined

  const runTrailingAction = useCallback((opt: ValueSelectorOption) => {
    if (opt.disabled || !opt.trailingAction) return
    setOpen(false)
    opt.trailingAction.onSelect()
  }, [])

  const selectEmptyOption = useCallback(() => {
    setOpen(false)
    onChange('')
  }, [onChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // In a side list, the arrows move through it; Left or Escape go back.
      const kinds = submenuParent?.children
      if (submenu && kinds && submenu.highlight !== null) {
        const at = submenu.highlight
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          const next =
            e.key === 'ArrowDown' ? Math.min(at + 1, kinds.length - 1) : Math.max(at - 1, 0)
          setSubmenu({ id: submenu.id, highlight: next })
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          if (kinds[at]) selectOption(kinds[at])
          return
        }
        if (e.key === 'ArrowLeft' || e.key === 'Escape') {
          e.preventDefault()
          setSubmenu(null)
          return
        }
      }
      switch (e.key) {
        case 'ArrowDown':
          setKeyboardNavigation(true)
          e.preventDefault()
          shouldScrollHighlightedRef.current = true
          setHighlightIndex((i) => Math.min(i + 1, Math.max(optionCount - 1, 0)))
          break
        case 'ArrowUp':
          setKeyboardNavigation(true)
          e.preventDefault()
          shouldScrollHighlightedRef.current = true
          setHighlightIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          if (showEmptyOption && highlightIndex === 0) {
            selectEmptyOption()
            break
          }
          {
            const optionIndex = showEmptyOption ? highlightIndex - 1 : highlightIndex
            const option = filtered[optionIndex]
            if (option?.children) setSubmenu({ id: option.id, highlight: 0 })
            else if (option) selectOption(option)
          }
          break
        case 'ArrowRight': {
          // Otherwise the arrow keeps moving the search field's text cursor.
          const option = filtered[showEmptyOption ? highlightIndex - 1 : highlightIndex]
          if (option?.children && !option.disabled) {
            e.preventDefault()
            setSubmenu({ id: option.id, highlight: 0 })
            break
          }
          if (!option?.trailingAction || option.disabled) break
          e.preventDefault()
          runTrailingAction(option)
          break
        }
        case 'Escape':
          e.preventDefault()
          setOpen(false)
          break
      }
    },
    [
      filtered,
      highlightIndex,
      optionCount,
      runTrailingAction,
      selectEmptyOption,
      selectOption,
      showEmptyOption,
      submenu,
      submenuParent,
    ]
  )

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (clearable && value && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault()
        onChange('')
        return
      }

      // Once open, the arrows, Enter, and Escape work the list from its
      // trigger: a list without a search field never has the keyboard itself.
      if (open) {
        // Space would click the trigger, closing the list it's working.
        if (e.key === ' ' && !opensFromChevron) e.preventDefault()
        else handleKeyDown(e)
        return
      }
      // A chevron trigger leaves its keys to the host while closed (a table
      // cell moves the selection on the arrows and opens on Enter).
      if (opensFromChevron) return
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen(true)
      }
    },
    [clearable, handleKeyDown, onChange, open, opensFromChevron, value]
  )

  return (
    <div
      ref={containerRef}
      className={clsx('relative shrink-0', className)}
      style={{ width: renderTrigger ? undefined : fullWidth ? '100%' : autoWidth }}
    >
      <ValueSelectorTrigger
        id={id}
        open={open}
        searchInTrigger={searchInTrigger}
        search={search}
        onSearchChange={(nextSearch) => {
          setSearch(nextSearch)
          shouldScrollHighlightedRef.current = false
          setHighlightIndex(0)
        }}
        onOpenChange={setOpen}
        onTriggerClick={toggleFromTrigger}
        onDropdownKeyDown={handleKeyDown}
        onTriggerKeyDown={handleTriggerKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        placeholderClassName={placeholderClassName}
        selectedOption={selectedOption}
        selectedDescription={selectedDescription}
        optionCards={optionCards}
        renderTrigger={renderTrigger}
        variant={variant}
        inlineLabel={inlineLabel}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        ariaLabel={ariaLabel}
        ariaInvalid={ariaInvalid}
        ariaRequired={ariaRequired}
        ariaDescribedBy={ariaDescribedBy}
        controlClassName={extraControlClassName}
        searchInputClassName={searchInputClassName}
        onInputRef={(node) => {
          inputRef.current = node
          setControlElement(node)
        }}
        setMeasuredElement={setControlElement}
      />

      {open && dropdownPosition ? (
        <ValueSelectorDropdown
          dropdownRef={dropdownRef}
          optionListRef={optionListRef}
          inputRef={inputRef}
          position={dropdownPosition}
          zIndex={dropdownZIndex ?? popupLayer}
          value={value}
          search={search}
          onSearchChange={(nextSearch) => {
            setSearch(nextSearch)
            shouldScrollHighlightedRef.current = false
            setHighlightIndex(0)
          }}
          onKeyDown={handleKeyDown}
          ariaLabel={ariaLabel}
          showSearch={showDropdownSearch}
          searchEnabled={searchEnabled}
          searchInputClassName={searchInputClassName}
          filtered={filtered}
          optionCount={optionCount}
          showEmptyOption={showEmptyOption}
          emptyOptionAriaLabel={emptyOptionAriaLabel}
          emptyLabel={emptyLabel}
          placeholder={placeholder}
          emptyOptionsMessage={emptyOptionsMessage}
          loadingMessage={loadingMessage}
          highlightIndex={highlightIndex}
          keyboardNavigation={keyboardNavigation}
          onHighlight={(index) => {
            shouldScrollHighlightedRef.current = false
            setHighlightIndex(index)
          }}
          onHover={(index) => {
            setKeyboardNavigation(false)
            shouldScrollHighlightedRef.current = false
            setHighlightIndex(index)
            // Pointing at a grouping option opens its side list; at any other, closes it.
            const option = filtered[showEmptyOption ? index - 1 : index]
            setSubmenu(
              option?.children && !option.disabled ? { id: option.id, highlight: null } : null
            )
          }}
          submenuParent={submenuParent}
          submenuHighlight={submenu?.highlight ?? null}
          submenuRef={submenuRef}
          onSubmenuHover={(index) => submenu && setSubmenu({ id: submenu.id, highlight: index })}
          onSelectEmpty={selectEmptyOption}
          onSelectOption={selectOption}
          onTrailingAction={runTrailingAction}
          optionClassName={optionClassName}
          emptyOptionClassName={emptyOptionClassName}
          messageClassName={messageClassName}
          optionCards={optionCards}
          variant={variant}
        />
      ) : null}
    </div>
  )
}
